import { Component, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, FormArray, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable ,  Subject } from 'rxjs';
import * as _ from 'lodash';

import { BrandService } from 'shared/brand/brand.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { Config } from 'shared/config/env.config';
import { PartNumberService } from 'shared/part-number/part-number.service';
import { languageLookup } from 'shared/product/product-lookups';
import { getFieldByMap } from 'utils/utils';

import { descriptionSegmentData } from '../../segment-data/description-segment-data';
import { kitsSegmentData } from '../../segment-data/kits-segment-data';
import { KitModel } from '../../models/kit.model';
import { BaseSegmentListComponent } from '../base/_base-segment-list.component';
import { segmentAnimation } from '../base/animations';
import { rowData, contentData, photoWidthPx, collapseWidthPx } from './table-data';
import {delay, mergeMap, takeWhile} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';


@Component({
  moduleId: module.id,
  selector: 'kits-segment',
  templateUrl: 'kits.component.html',
  styleUrls: ['../base/_base-segment.component.scss', 'kits.component.scss'],
  animations: [segmentAnimation],
})
export class KitsComponent extends BaseSegmentListComponent {
  rowData: any[] = rowData;
  contentData: any[] = contentData;
  photoWidth = photoWidthPx;
  collapseWidth = collapseWidthPx;

  // define in the Child
  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'kits'; // used in save request
  pathToScores: string = 'segments.kits.tallies';
  controlClassPrefix: string = 'kit-control-row-';

  segmentData: any = kitsSegmentData;

  availableDescrTypes: Array<string> = ['SHO', 'DES', 'INV', 'EXT', 'ABR', 'LAB'];

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    protected partNumberService: PartNumberService,
    protected sanitizer: DomSanitizer,
  ) {
    super(ref, scorecardConnectorService, brandService, fb, sanitizer);
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getSegmentFormData(): any {
    const kits = this.formArray.value;
    return _.map(kits, (kit: any) => {
      return _.pick(kit, [
        'componentBrand', 'componentSubBrand', 'componentPartNumber',
        'description', 'descriptionLanguage', 'descriptionType',
        'partType', 'quantityInKit', 'quantityInKitUom', 'soldSeparately',
        'componentIdQualifier',
      ]);
    });
  }

  public getKitFieldLookup(fieldMap: string, formGroup: UntypedFormGroup): any[] {
    if (fieldMap === 'descriptionType') {
      return formGroup.get('availableDescrTypes').value;
    } else if (fieldMap === 'descriptionLanguage') {
      return formGroup.get('availableDescrLangs').value;
    } else {
      return this.getFieldLookup(fieldMap);
    }
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/
  // replace parent handler:
  onSelectedValueChanged(value, fieldName, index) {
    if ((fieldName === 'descriptionType' || fieldName === 'descriptionLanguage')) {
      this.updateDescriptionByIndex(index);
    }
  }

  onSelectTypeahead(event, fieldName: string, formGroup: UntypedFormGroup, index: number) {
    if (fieldName === 'componentPartNumber') {
      this.handlePartNumberChanged(event.item, formGroup);
      _.set(this.itemDetailData[index], 'component_part_number.value', _.get(event, 'item.partNumber'));
      this.ref.markForCheck();
      this.scorecardConnectorService.detectChangesInScorecard.emit();
    }
  }

  onControlValueChanged(value, fieldName, index) {
    if ((fieldName === 'descriptionType' || fieldName === 'descriptionLanguage')) {
      this.updateDescriptionByIndex(index);
    }
  }

  onKitControlStatusChange(status: any, itemName: string, index: number) {
    if (itemName === 'componentBrandLabel') {
      // validate brand instead of label
      itemName = 'componentBrand';
    }
    const item = getFieldByMap(itemName, this.segmentData);
    this.scorecardConnectorService.controlStatusChanged.emit({
      status, index, item,
    });
  }

  onKitControlPopulatedChange(populated: any, itemName: string, index: number) {
    if (itemName === 'componentBrandLabel') {
      // populate brand instead of label
      itemName = 'componentBrand';
    }
    const item = getFieldByMap(itemName, this.segmentData);
    this.scorecardConnectorService.controlPopulatedChanged.emit({
      populated, index, item,
    });
  }

  onTypeaheadControlBlured(event: any, fieldName: string, formGroup: UntypedFormGroup, index: number) {
    if (fieldName === 'componentPartNumber') {
      // if typeahead wasn't selected - reset an old value
      if (_.get(this.itemDetailData[index], 'component_part_number.value') !== formGroup.get('componentPartNumber').value) {
        formGroup.patchValue({
          'componentPartNumber': _.get(this.itemDetailData[index], 'component_part_number.value'),
        });
        this.ref.markForCheck();
      }
    }
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  // replace parent method:
  protected setData(itemDetail: any = {}): void {
    if (this.itemDetailData) {
      _.forEach(this.itemDetailData, (kit: any) => {
        const partNumber = _.get(kit, 'component_part_number.value');
        kit.photo = this.getKitThumbnail(partNumber);
      });
      this.ref.markForCheck();
      this.updateForm().subscribe(() => {
        this.showSegment();
      });
    }
  }

  // replace parent method:
  protected handleDataItem(item: any) {
    const handledDataItem = {...item};

    // set photo
    const partNumber = _.get(handledDataItem, 'component_part_number.value');
    handledDataItem.photo = this.getKitThumbnail(partNumber);

    // find available descriptions for this kit
    handledDataItem.availableDescrTypes = this.getAvailableDescrTypes(item.partData);
    handledDataItem.availableDescrList = item.partData.descriptions;
    this.handleSelectedDescriptionType(handledDataItem);

    return handledDataItem;
  }

  // replace parent method:
  protected createItemModel(item: any): KitModel {
    const model = new KitModel();

    _.each(this.segmentData, (field: any) => {
      if (!field.field_map) { return; }

      // add typeahead to the model
      if (_.has(field, 'api_lookup.lookup')) {

        _.set(model, field.api_lookup.field_map, new UntypedFormControl(_.get(field.api_lookup, 'default_value', '')) );

        const dataSource = new Observable((observer: any) => {
            // Runs on every search
            observer.next(model.componentPartNumber.value);
          })
          .pipe(mergeMap((token: string) => {
            return this.partNumberService.search(this.brandService.getBrand(), token);
          }));

        _.set(model, `${field.api_lookup.field_map}_dataSource`, new UntypedFormControl(dataSource) );
      }

      const validators: any = this.getControlValidators(field);
      const value = _.get(item, field.item_name, '');
      _.set(model, field.field_map, new UntypedFormControl(value, validators));
    });
    // add photo value
    _.set(model, 'photo', new UntypedFormControl(_.get(item, 'photo', ''), null));
    _.set(model, 'availableDescrList', new UntypedFormControl(_.get(item, 'availableDescrList', [])));
    _.set(model, 'availableDescrTypes', new UntypedFormControl(_.get(item, 'availableDescrTypes', [])));
    _.set(model, 'availableDescrLangs', new UntypedFormControl( _.get(item, 'availableDescrLangs', [])));

    return model;
  }

  // replace parent method:
  protected createItem() {
    return {
      component_brand: null,
      component_brand_label: null,
      component_part_number: {id_qualifier: null, value: null},
      component_sub_brand: null,
      component_sub_brand_label: null,
      description: {language: null, type: null, value: null},
      part_terminology_id: null,
      part_type: null,
      quantity_in_kit: {uom: null, quantity: null},
    };
  }
  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private getKitThumbnail(partNumber: string): string {
    let thumbnail: string = Config.DEFAULT_KIT_IMAGE;
    const brandRelationshipId = this.brandService.getBrand();
    if (partNumber && brandRelationshipId) {
      thumbnail = `${Config.BACKEND}parts/assets/part-number/${brandRelationshipId}/100?status=published&partNumber=${partNumber}`;
    }
    return thumbnail;
  }

  private updateForm(): Observable<any> {
    const subject = new Subject();

    // create formArray
    this.segmentForm.setControl(this.segmentName, this.fb.array([]));

    const requestArray = [];
    const interval = 100;
    let timer = 0;

    _.forEach(this.itemDetailData, (item: any, i: number) => {
      const partNumber = _.get(item, 'component_part_number.value');

      requestArray.push(this.partNumberService.fetch(this.brandService.getBrand(), partNumber).pipe(delay(timer)));
      /* throttle the calls so we do not reach the servers throttle limit */
      timer = interval * i++;
    });

    forkJoin(requestArray)
      .pipe(takeWhile(() => this.isActive))
      .subscribe((partData: any[]) => {
        // handle each data item
        this.itemDetail[this.segmentName] = this.itemDetailData.map((item: any, index: number) => {
          item.partData = partData[index];
          return this.handleDataItem(item);
        });

        // add data item to formArray
        _.forEach(this.itemDetailData, (item: any) => {
          this.formArray.push(this.createItemFG(item));
        });
        this.ref.markForCheck();
        subject.next();
      });

    return subject.asObservable();
  }

  private handlePartNumberChanged(partData: any, formGroup: UntypedFormGroup) {
    // populate data
    // change select items lists: this.descriptionSelect[index], this.languageSelect[index]
    // change brand, sub brand, part terminology:

    const partTypeCategory = _.get(partData, 'partTerminology.category.name', '');
    const partTypeSubCategory = _.get(partData, 'partTerminology.subcategory.name', '');
    const brandId = _.get(partData, 'brandId', '');
    const subBrandId = _.get(partData, 'details.subBrand', '');
    const brandLabel = _.get(partData, 'details.brandLabel', '');
    const subBrandLabel = _.get(partData, 'details.subBrandLabel', '');


    /*
     * Build the Part Type string
     */
    let partType = partTypeCategory;
    if (partTypeSubCategory) {
      partType += '/' + partTypeSubCategory;
    }

    /*
     * Update the the form controls with the selected brand and part type
     */

    // if none description selected
    // set description type, lang & value by default
    if (!_.get(formGroup.get('description'), 'value.length')) {
      const type = _.get(_.keys(partData.descriptions), '[0]');
      if (type) {
        formGroup.get('descriptionType').setValue(type);
        const lang = _.get(_.keys(partData.descriptions[type]), '[0]');
        if (lang) {
          formGroup.get('descriptionLanguage').setValue(lang);
          formGroup.get('description').setValue(_.get(partData, `descriptions[${type}][${lang}][0]`));
        }
      }
    }

    formGroup.patchValue({
      partData: partData,
      componentBrand: brandId,
      componentBrandLabel: brandLabel,
      componentSubBrand: subBrandId,
      componentSubBrandLabel: subBrandLabel,
      partType,
      availableDescrTypes: this.getAvailableDescrTypes(partData),
      availableDescrLangs: this.getAvailableDescrLangs(partData, formGroup.get('descriptionType').value),
      availableDescrList: partData.descriptions,
      photo: this.getKitThumbnail(partData.partNumber),
    });

    this.ref.markForCheck();
    this.updateDescription(formGroup);
  }

  private getDescrSegmentValues(types: Array<string>): Array<any> {
    const descrSegmentValues = _.filter(descriptionSegmentData, (description: any) => {
      return (
        _.indexOf(this.availableDescrTypes, description.field_map.toUpperCase()) > -1 &&
        _.indexOf(types, description.field_map.toUpperCase()) > -1
      );
    });
    return descrSegmentValues;
  }

  private getLanguagesLookup(languages: Array<string>): Array<any> {
    return _.intersectionWith(languageLookup, languages, (lookup, lang) => {
      return lookup.option === lang;
    });
  }

  // if description type was selected - set the languages list
  private handleSelectedDescriptionType(item: any) {
    const descriptionType = _.get(item, 'description.type');
    if (descriptionType && item.partData.descriptions) {
      item.availableDescrLangs = this.getAvailableDescrLangs(item.partData, descriptionType);
      this.ref.markForCheck();
    }
  }

  private updateDescriptionByIndex(index: number) {
    this.updateDescription(this.formArray.at(index) as UntypedFormGroup);
  }

  // update 'description' value for kits[index]
  // based on current description type and language values
  private updateDescription(formGroup: UntypedFormGroup) {
    const formGroupVal = formGroup.value;
    if (!formGroupVal) { return; }

    const descrType = _.get(formGroupVal, 'descriptionType');
    const descrLang = _.get(formGroupVal, 'descriptionLanguage');
    const availableDescrList = _.get(formGroupVal, 'availableDescrList');

    if (descrType && descrLang && availableDescrList) {
      let descr = _.get(availableDescrList, `${descrType}.${descrLang}`);
      if (Array.isArray(descr)) { descr = descr[0]; }

      if (descr) {
        formGroup.get('description').patchValue(descr);
        this.ref.markForCheck();
      }
    } else {
      formGroup.get('description').patchValue('');
      this.ref.markForCheck();
    }
    this.scorecardConnectorService.detectChangesInScorecard.emit();
  }

  // get list of available description types from partData
  // e.g. [{option: "SHO", title: "Short description"}, {option: "INV", title: "Invoice description"}]
  private getAvailableDescrTypes(partData: any) {
    const descrTypes = _.keys(partData.descriptions);
    const descrSegmentValues: Array<any> = this.getDescrSegmentValues(descrTypes);
    return _.map(descrSegmentValues, (descrSegmentValue: any) => {
      return {
        option: descrSegmentValue.field_map.toUpperCase(),
        title: descrSegmentValue.title,
      };
    });
  }

  // get list of available description types based on partData and selected descriptionType
  private getAvailableDescrLangs(partData: any, descriptionType: string) {
    const langKeys = _.keys(_.get(partData, `descriptions.${descriptionType}`));
    return this.getLanguagesLookup(langKeys);
  }
}
